/**
 * AccumulationLayer — generic world-up masked surface layer (snow, dust,
 * ash, sand) applied onto any node material.
 *
 * mask = coverage-shaped smoothstep of the world normal's upness, broken up
 * by stable object-space noise so the layer edge is organic, then the layer
 * colour/roughness lerp over the material's own (materialColor /
 * materialRoughness accessors — composes with maps untouched).
 *
 * The atlas snow layer (wetness.ts) hand-rolls this per-material; this is
 * the engine-generic modifier, and the `dust` preset closes the audit's
 * missing dust-accumulation item.
 */

import type * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec3, vec4, normalWorld, positionLocal,
  materialColor, materialRoughness, mx_noise_float, smoothstep, saturate, mix, pow,
} from 'three/tsl';

export interface AccumulationConfig {
  /** Layer colour (default snow-white 0.92,0.93,0.95). */
  color?: [number, number, number];
  /** Layer roughness (default 0.7). */
  roughness?: number;
  /** Coverage 0..1 — 0 none, 1 everything up-facing (default 0.5). */
  coverage?: number;
  /** Upness exponent — higher confines the layer to flatter surfaces (default 2). */
  upBias?: number;
  /** Noise breakup frequency (default 4). */
  noiseScale?: number;
  /** Noise influence on the mask edge 0..1 (default 0.35). */
  noiseStrength?: number;
}

export interface AccumulationHandle {
  color: { value: THREE.Vector3 };
  roughness: { value: number };
  coverage: { value: number };
  upBias: { value: number };
  noiseScale: { value: number };
  noiseStrength: { value: number };
}

/** CPU mirror of the mask (exported for tests). noise ∈ [0,1]. */
export function accumulationMaskCpu(
  normalY: number, coverage: number, upBias: number,
  noise: number, noiseStrength: number,
): number {
  const upness = Math.pow(Math.max(normalY, 0), upBias);
  const threshold = 1 - coverage;
  const jitter = (noise - 0.5) * noiseStrength;
  const t = (upness - (threshold + jitter)) / 0.15;
  const s = t < 0 ? 0 : (t > 1 ? 1 : t);
  const c = coverage <= 0 ? 0 : coverage >= 0.1 ? 1 : coverage / 0.1;
  return s * s * (3 - 2 * s) * (c * c * (3 - 2 * c));
}

export function applyAccumulationLayer(
  material: THREE.Material & { colorNode?: unknown; roughnessNode?: unknown },
  config?: AccumulationConfig,
): AccumulationHandle {
  const c = config?.color ?? [0.92, 0.93, 0.95];
  const uColor = uniform(vec3(c[0], c[1], c[2]));
  const uRoughness = uniform(config?.roughness ?? 0.7);
  const uCoverage = uniform(config?.coverage ?? 0.5);
  const uUpBias = uniform(config?.upBias ?? 2);
  const uNoiseScale = uniform(config?.noiseScale ?? 4);
  const uNoiseStrength = uniform(config?.noiseStrength ?? 0.35);

  const maskNode = Fn(() => {
    const upness = pow(saturate(normalWorld.y), uUpBias);
    const noise = mx_noise_float(positionLocal.mul(uNoiseScale)).mul(0.5).add(0.5);
    const jitter = noise.sub(0.5).mul(uNoiseStrength);
    const threshold = float(1).sub(uCoverage).add(jitter);
    // WHY the coverage fade: the noise jitter dropped the threshold below 1 at coverage 0, so every flat up-facing
    // surface wore snow patches in clear weather (Atlas drives coverage 0 on a clear day). The layer now fades in over
    // the first 10 % of coverage — 0 is none, as documented; ≥ 0.1 is unchanged.
    return smoothstep(threshold, threshold.add(0.15), upness).mul(smoothstep(0, 0.1, uCoverage));
  })();

  material.colorNode = Fn(() => {
    const base = vec4(materialColor);
    return vec4(mix(base.rgb, uColor, maskNode), base.a);
  })();
  material.roughnessNode = Fn(() => {
    return mix(materialRoughness, uRoughness, maskNode);
  })();
  material.needsUpdate = true;

  return {
    color: uColor as unknown as { value: THREE.Vector3 },
    roughness: uRoughness as unknown as { value: number },
    coverage: uCoverage as unknown as { value: number },
    upBias: uUpBias as unknown as { value: number },
    noiseScale: uNoiseScale as unknown as { value: number },
    noiseStrength: uNoiseStrength as unknown as { value: number },
  };
}

/** Dust preset: warm grey, high roughness, gentler up bias. */
export function applyDustLayer(
  material: THREE.Material & { colorNode?: unknown; roughnessNode?: unknown },
  config?: Partial<AccumulationConfig>,
): AccumulationHandle {
  return applyAccumulationLayer(material, {
    color: config?.color ?? [0.45, 0.4, 0.34],
    roughness: config?.roughness ?? 0.95,
    coverage: config?.coverage ?? 0.35,
    upBias: config?.upBias ?? 1.4,
    noiseScale: config?.noiseScale ?? 6,
    noiseStrength: config?.noiseStrength ?? 0.5,
  });
}
